(() => {
  const root = document.querySelector('[data-email-signup-popup]');
  if (!root) return;

  const configEl = document.getElementById('EmailSignupPopupConfig');
  let config = {};
  try {
    config = configEl ? JSON.parse(configEl.textContent) : {};
  } catch (error) {
    config = {};
  }

  const autoOpen = config.autoOpen === true || root.dataset.emailSignupPopupAutoOpen === 'true';
  let popupPreparation = null;
  let popupStart = null;
  let popupReady = false;
  let manualOpenRequested = false;
  let closeBound = false;
  let lastTrigger = null;

  const panel = root.querySelector('[data-email-signup-panel]');
  const stage = root.querySelector('[data-email-signup-stage]');
  const art = root.querySelector('[data-email-signup-art]');
  const form = root.querySelector('#EmailSignupPopupForm');
  const input = root.querySelector('[data-email-signup-input]');
  const BREAKPOINT = config.breakpoint || 750;
  const UNTIL_KEY = config.storageUntilKey || 'prn-email-popup-until';
  const LAST_KEY = config.storageLastIdKey || 'prn-email-popup-last-id';
  const DELAY = Number(config.delayMs) || 20000;
  const SUPPRESS_MS = Number(config.suppressMs) || 86400000;
  const SUCCESS_MS = Number(config.successMs) || 4500;
  const MAX_PROBES = 4;
  const KLAVIYO_TIMEOUT = 2500;
  const CLOSE_MS = 240;
  const CONSENT_BANNER_ID = 'shopify-pc__banner';
  const CONSENT_WAIT_MS = 60000;
  const CONSENT_SETTLE_MS = 500;
  const POST_CONSENT_MS = 1200;

  const parseUrlMap = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return {};
    }
  };

  const lookupUrl = (map, id) => {
    if (!map) return '';
    return (map[id] || map[String(Number(id))] || '').trim();
  };

  const sizedUrl = (url, width) => {
    if (!url) return '';
    const joiner = url.includes('?') ? '&' : '?';
    return `${url}${joiner}width=${width}`;
  };

  const forceParam = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('popup');
  };

  const deviceForViewport = () => (window.matchMedia(`(min-width: ${BREAKPOINT}px)`).matches ? 'desktop' : 'mobile');

  const isSuppressed = () => {
    const until = Number(window.localStorage.getItem(UNTIL_KEY) || 0);
    return until > Date.now();
  };

  const suppress = (ms = SUPPRESS_MS) => {
    window.localStorage.setItem(UNTIL_KEY, String(Date.now() + ms));
  };

  const rememberLast = (id) => {
    window.localStorage.setItem(LAST_KEY, id);
  };

  const imageAttrs = (design) => {
    const mobile = design.device === 'mobile';
    const oneX = mobile ? 648 : 1280;
    const twoX = mobile ? 1296 : 2560;
    return {
      mobile,
      oneX,
      twoX,
      src: sizedUrl(design.imageUrl, oneX),
      srcset: `${sizedUrl(design.imageUrl, oneX)} ${oneX}w, ${sizedUrl(design.imageUrl, twoX)} ${twoX}w`,
      sizes: mobile
        ? 'min(86vw, calc(82vh * 648 / 1152))'
        : 'min(80vw, calc(76vh * 1280 / 894.34))',
    };
  };

  const applyFontFromStage = () => {
    const width = stage.getBoundingClientRect().width;
    if (!width) return;
    const pct = Number(stage.style.getPropertyValue('--email-signup-font-pct') || 2.688);
    const capRatio = 1365 / 2048;
    stage.style.setProperty('--email-font-px', `${((pct / 100) * width) / capRatio}px`);
  };

  const applyDesign = (design) => {
    if (!design || !design.imageUrl) return false;
    const attrs = imageAttrs(design);
    stage.classList.toggle('email-signup-popup__stage--mobile', attrs.mobile);
    stage.classList.toggle('email-signup-popup__stage--desktop', !attrs.mobile);
    stage.style.setProperty('--email-signup-font-pct', String(design.fontSizePctWidth || 2.688));
    // A few comps bake white type onto dark artwork; the typed value has to match.
    stage.style.setProperty('--email-ink', design.ink === 'light' ? '#fff' : '#000');
    stage.style.setProperty('--email-input-left', `${design.input.left}%`);
    stage.style.setProperty('--email-input-right', `${design.input.right}%`);
    stage.style.setProperty('--email-input-center-y', `${design.input.centerY}%`);
    stage.style.setProperty('--email-submit-left', `${design.submit.left}%`);
    stage.style.setProperty('--email-submit-right', `${design.submit.right}%`);
    stage.style.setProperty('--email-submit-top', `${design.submit.top}%`);
    stage.style.setProperty('--email-submit-bottom', `${design.submit.bottom}%`);
    art.srcset = attrs.srcset;
    art.sizes = attrs.sizes;
    art.src = attrs.src;
    art.width = Math.round(design.pageWidth || attrs.oneX);
    art.height = Math.round(design.pageHeight || (attrs.mobile ? 1152 : 894));
    applyFontFromStage();
    requestAnimationFrame(applyFontFromStage);
    art.addEventListener('load', applyFontFromStage);
    window.addEventListener('resize', applyFontFromStage);
    return true;
  };

  const shuffle = (items) => {
    const list = items.slice();
    for (let i = list.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      const swap = list[i];
      list[i] = list[j];
      list[j] = swap;
    }
    return list;
  };

  const orderPool = (pool, forcedId) => {
    if (forcedId && forcedId !== '1') {
      const match = pool.find((item) => item.id === forcedId.padStart(3, '0') || item.id === forcedId);
      const rest = shuffle(pool.filter((item) => item !== match));
      return match ? [match, ...rest] : rest;
    }
    const last = window.localStorage.getItem(LAST_KEY);
    const preferred = pool.filter((item) => item.id !== last);
    const lastItem = pool.find((item) => item.id === last);
    if (!preferred.length) return shuffle(pool);
    const ordered = shuffle(preferred);
    if (lastItem) ordered.push(lastItem);
    return ordered;
  };

  const probeDesign = (design) =>
    new Promise((resolve, reject) => {
      const attrs = imageAttrs(design);
      if (!attrs.src) {
        reject();
        return;
      }
      const img = new Image();
      img.onload = () => resolve(design);
      img.onerror = () => reject();
      img.sizes = attrs.sizes;
      img.srcset = attrs.srcset;
      img.src = attrs.src;
    });

  const findLoadableDesign = async (queue) => {
    const limit = Math.min(MAX_PROBES, queue.length);
    for (let i = 0; i < limit; i += 1) {
      try {
        return await probeDesign(queue[i]);
      } catch (error) {
        // next candidate
      }
    }
    return null;
  };

  // Shopify's cookie banner sits at z-index 2000000 and covers the lower third of
  // the screen, which is exactly where the mobile comps put the email field. Hold
  // the popup until the visitor has answered it.
  const consentBannerVisible = () => {
    const banner = document.getElementById(CONSENT_BANNER_ID);
    if (!banner) return false;
    const rect = banner.getBoundingClientRect();
    if (!rect.height || !rect.width) return false;
    const styles = window.getComputedStyle(banner);
    return styles.visibility !== 'hidden' && styles.display !== 'none';
  };

  const waitForConsent = () =>
    new Promise((resolve) => {
      if (!consentBannerVisible()) {
        resolve('none');
        return;
      }
      const deadline = Date.now() + CONSENT_WAIT_MS;
      const finish = (outcome) => {
        window.clearInterval(timer);
        document.removeEventListener('visitorConsentCollected', onConsent);
        resolve(outcome);
      };
      const onConsent = () => {
        window.setTimeout(() => {
          if (!consentBannerVisible()) finish('dismissed');
        }, CONSENT_SETTLE_MS);
      };
      const timer = window.setInterval(() => {
        if (!consentBannerVisible()) finish('dismissed');
        else if (Date.now() > deadline) finish('timeout');
      }, 400);
      document.addEventListener('visitorConsentCollected', onConsent);
    });

  const open = (trigger = null) => {
    if (trigger) lastTrigger = trigger;
    root.hidden = false;
    root.classList.remove('is-closing');
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('email-signup-popup-open');
    applyFontFromStage();
    requestAnimationFrame(applyFontFromStage);
    window.setTimeout(() => {
      // Focus the dialog, not the field: auto-focusing the input paints a focus
      // ring over the comp and pops the mobile keyboard before anyone asked.
      if (typeof trapFocus === 'function') {
        trapFocus(panel, panel);
      } else {
        panel.focus();
      }
    }, 0);
  };

  const close = () => {
    if (root.hidden || root.classList.contains('is-closing')) return;
    // Hold `is-open` (and so `display: flex`) until the fade-out has played.
    root.classList.add('is-closing');
    root.setAttribute('aria-hidden', 'true');
    suppress();
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    window.setTimeout(() => {
      root.classList.remove('is-open');
      root.classList.remove('is-closing');
      root.hidden = true;
      document.body.classList.remove('email-signup-popup-open');
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
      lastTrigger = null;
    }, CLOSE_MS);
  };

  const subscribeWithKlaviyo = (email) => {
    const publicKey = String(config.klaviyoPublicKey || '').trim();
    const listId = String(config.klaviyoListId || '').trim();
    if (!config.klaviyoEnabled || !publicKey || !listId || !email) return Promise.resolve();
    if (/^pk_/i.test(publicKey)) {
      console.warn('Email signup popup: Klaviyo public key looks private; subscribe skipped.');
      return Promise.resolve();
    }

    const body = JSON.stringify({
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
          custom_source: 'PRNGRPHY homepage popup',
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    });

    return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicKey)}`, {
      method: 'POST',
      keepalive: true,
      headers: {
        revision: '2024-10-15',
        'content-type': 'application/json',
      },
      body,
    }).then((response) => {
      if (response.ok) {
        document.dispatchEvent(new CustomEvent('newsletterSignupSuccess'));
      }
      return response;
    }).catch(() => {
      console.warn('Email signup popup: Klaviyo subscribe failed.');
    });
  };

  const bindClose = () => {
    if (closeBound) return;
    closeBound = true;

    root.querySelectorAll('[data-email-signup-close]').forEach((el) => {
      el.addEventListener('click', (event) => {
        event.preventDefault();
        close();
      });
    });

    // Anything outside the panel is the backdrop now.
    root.addEventListener('click', (event) => {
      if (event.target === root) close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !root.hidden) close();
    });
  };

  const bind = (design) => {
    bindClose();

    if (form) {
      let releasing = false;
      form.addEventListener('submit', (event) => {
        if (releasing) return;
        const email = input?.value?.trim();
        if (!email || (typeof form.checkValidity === 'function' && !form.checkValidity())) return;

        // Shopify's customer form posts as a real navigation (its CAPTCHA needs
        // that). A navigation cancels the Klaviyo request even with keepalive, so
        // hold the submit, let Klaviyo answer, then re-dispatch the same submit.
        event.preventDefault();
        releasing = true;
        rememberLast(design.id);
        suppress();
        root.classList.add('is-sending');

        const release = () => {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.submit();
          }
        };

        let pending;
        try {
          pending = subscribeWithKlaviyo(email);
        } catch (error) {
          pending = null;
        }
        if (!pending || typeof pending.then !== 'function') {
          release();
          return;
        }
        const guard = new Promise((resolve) => {
          window.setTimeout(resolve, KLAVIYO_TIMEOUT);
        });
        Promise.race([pending, guard]).then(release, release);
      });
    }
  };

  const mergeUrls = (designs, fileUrlMap, overrideMap) =>
    designs
      .map((design) => {
        const override = lookupUrl(overrideMap, design.id);
        const fromFiles = lookupUrl(fileUrlMap, design.id);
        return { ...design, imageUrl: override || fromFiles || (design.imageUrl || '').trim() };
      })
      .filter((design) => design.imageUrl);

  const waitForOpenTime = (readyAt) => {
    const remaining = readyAt - Date.now();
    return new Promise((resolve) => {
      window.setTimeout(resolve, Math.max(0, remaining));
    });
  };

  const preparePopup = async () => {
    if (popupReady) return true;
    if (popupPreparation) return popupPreparation;

    popupPreparation = (async () => {
      const success = Boolean(form?.querySelector('[data-email-signup-success]'));
      let catalog = { designs: [] };
      try {
        const response = await fetch(config.designsAsset);
        catalog = await response.json();
      } catch (error) {
        return false;
      }

      const fileUrlMap = parseUrlMap(config.fileUrlMap);
      const overrideMap = parseUrlMap(config.urlMap);
      const device = deviceForViewport();
      const pool = mergeUrls(catalog.designs || [], fileUrlMap, overrideMap).filter((item) => item.device === device);
      if (!pool.length) return false;

      const force = forceParam();
      const lastId = window.localStorage.getItem(LAST_KEY);
      const queue = orderPool(pool, success ? lastId : force);
      const design = await findLoadableDesign(queue);
      if (!design || !applyDesign(design)) {
        console.warn('Email signup popup: no artwork loaded.');
        return false;
      }

      if (success) {
        root.classList.add('is-posted');
        bindClose();
      } else {
        bind(design);
        rememberLast(design.id);
      }

      popupReady = true;
      return true;
    })().finally(() => {
      popupPreparation = null;
    });

    return popupPreparation;
  };

  const start = async ({ manual = false, trigger = null } = {}) => {
    const force = forceParam();
    const success = Boolean(form?.querySelector('[data-email-signup-success]'));
    if (manual) manualOpenRequested = true;
    if (success) suppress();
    if (!manual && !force && !success && isSuppressed()) return false;

    const readyAt = manual || success ? Date.now() : Date.now() + DELAY;
    if (!await preparePopup()) return false;

    // Manual card triggers bypass the homepage delay and suppression timestamp.
    if (manual) {
      const consent = await waitForConsent();
      if (consent === 'timeout') return false;
      if (consent === 'dismissed') await waitForOpenTime(Date.now() + POST_CONSENT_MS);
      if (root.classList.contains('is-open')) return true;
      open(trigger);
      return true;
    }

    // Wait out the open delay first: Shopify injects its cookie banner
    // asynchronously, so checking any earlier can miss it entirely.
    await waitForOpenTime(readyAt);
    if (manualOpenRequested || root.classList.contains('is-open')) return false;
    const consent = await waitForConsent();
    if (consent === 'timeout') return false;
    if (manualOpenRequested || root.classList.contains('is-open')) return false;
    if (consent === 'dismissed') await waitForOpenTime(Date.now() + POST_CONSENT_MS);
    if (manualOpenRequested || root.classList.contains('is-open')) return false;

    open();
    if (success) window.setTimeout(close, SUCCESS_MS);
    return true;
  };

  const launch = (options = {}) => {
    if (root.classList.contains('is-open') || root.classList.contains('is-closing')) return Promise.resolve(false);
    if (options.manual) {
      manualOpenRequested = true;
      if (popupReady) {
        open(options.trigger || null);
        return Promise.resolve(true);
      }
      return start(options);
    }
    if (popupStart) return popupStart;
    popupStart = start(options).finally(() => {
      popupStart = null;
    });
    return popupStart;
  };

  window.openEmailSignupPopup = (trigger = null) => launch({ manual: true, trigger });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-email-signup-popup-trigger]');
    if (!trigger) return;
    event.preventDefault();
    window.openEmailSignupPopup(trigger);
  });

  const begin = () => {
    if (autoOpen || forceParam()) launch();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', begin);
  } else {
    begin();
  }
})();
